import { useRef, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import { CHAT_REACTION_EMOJIS } from '@/config';
import { getAvatarBgColor, getInitials } from '@/utils/chat-avatar-color';
import ChatReactionPicker from './ChatReactionPicker';
import DeleteChatMessageModal from './DeleteChatMessageModal';
import type { DayTradingSession, DayTradingChatMessage } from '@/models/day-trading-session';
import type { DayTradingSessionID, UserID } from '@/models/ids';

interface ChatMessageItemProps {
  message: DayTradingChatMessage;
  sessionId: DayTradingSessionID;
  currentUserId: UserID;
  canReact: boolean;
  canDelete: boolean;
  onSessionUpdate: (session: DayTradingSession) => void;
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

const URL_REGEX = /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]}])/gi;

function renderTextWithLinks(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    parts.push(
      <a
        key={`${match.index}-${url}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline break-all"
      >
        {url}
      </a>,
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

interface GroupedReaction {
  emoji: string;
  codepoint: number;
  count: number;
  currentUserReactionId: string | null;
}

function groupReactions(message: DayTradingChatMessage, currentUserId: UserID): GroupedReaction[] {
  const groups = new Map<number, GroupedReaction>();
  for (const r of message.reactions) {
    let g = groups.get(r.emoji);
    if (g === undefined) {
      g = {
        emoji: String.fromCodePoint(r.emoji),
        codepoint: r.emoji,
        count: 0,
        currentUserReactionId: null,
      };
      groups.set(r.emoji, g);
    }
    g.count++;
    if (r.userID === currentUserId) {
      g.currentUserReactionId = r.id;
    }
  }
  return Array.from(groups.values());
}

export default function ChatMessageItem({
  message,
  sessionId,
  currentUserId,
  canReact,
  canDelete,
  onSessionUpdate,
}: ChatMessageItemProps) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerAnchorRef = useRef<HTMLButtonElement>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [imageLightbox, setImageLightbox] = useState(false);

  const grouped = groupReactions(message, currentUserId);

  const handleToggleReaction = async (emoji: string) => {
    const codepoint = emoji.codePointAt(0);
    if (codepoint === undefined) return;

    const existing = grouped.find((g) => g.codepoint === codepoint);
    try {
      let session: DayTradingSession;
      if (existing !== undefined && existing.currentUserReactionId !== null) {
        session = await APIRoutes.removeDayTradingChatReaction(
          sessionId,
          message.id,
          existing.currentUserReactionId as import('@/models/ids').DayTradingChatReactionID,
        );
      } else {
        session = await APIRoutes.reactToDayTradingChatMessage(sessionId, message.id, codepoint);
      }
      onSessionUpdate(session);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Reagieren');
    }
  };

  const bgColor = getAvatarBgColor(message.userID);
  const initials = getInitials(message.name);
  const imageUrl = message.imageID !== null ? APIRoutes.getBlobContentUrl(message.imageID) : null;

  return (
    <div className="group relative px-3 py-2 flex gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
      {/* Avatar */}
      <div
        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold select-none"
        style={{ backgroundColor: bgColor }}
        title={message.name}
      >
        {initials}
      </div>

      {/* Message body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{message.name}</span>
          <span className="text-[0.7rem] text-gray-400 dark:text-gray-500 shrink-0 tabular-nums">
            {formatTime(message.created)}
          </span>
          {message.edited !== null && (
            <span className="text-[0.65rem] text-gray-400 dark:text-gray-500">(bearbeitet)</span>
          )}
        </div>

        {message.text !== '' && (
          <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
            {renderTextWithLinks(message.text)}
          </div>
        )}

        {imageUrl !== null && (
          <div className="mt-1 max-w-xs">
            <img
              src={imageUrl}
              alt="Angehängtes Bild"
              className="max-w-full max-h-64 rounded-lg cursor-pointer border border-gray-200 dark:border-gray-700"
              onClick={() => setImageLightbox(true)}
            />
          </div>
        )}

        {/* Reaction chips */}
        {grouped.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {grouped.map((g) => {
              const mine = g.currentUserReactionId !== null;
              return (
                <button
                  key={g.codepoint}
                  onClick={() => canReact && handleToggleReaction(g.emoji)}
                  disabled={!canReact}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                    mine
                      ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600 text-blue-800 dark:text-blue-200'
                      : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  } ${canReact ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <span className="leading-none">{g.emoji}</span>
                  <span className="tabular-nums leading-none">{g.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Hover toolbar */}
      {(canReact || canDelete) && (
        <div className="absolute top-1 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm px-1 py-0.5">
          {canReact && (
            <>
              {CHAT_REACTION_EMOJIS.slice(0, 3).map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleToggleReaction(emoji)}
                  className="text-base p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer leading-none"
                  title={`Reagieren mit ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
              <button
                ref={pickerAnchorRef}
                onClick={() => setShowPicker((v) => !v)}
                className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
                title="Weitere Reaktionen"
              >
                <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
              </button>
              {showPicker && (
                <ChatReactionPicker
                  anchorRef={pickerAnchorRef}
                  onSelect={handleToggleReaction}
                  onClose={() => setShowPicker(false)}
                />
              )}
            </>
          )}
          {canDelete && (
            <button
              onClick={() => setShowDelete(true)}
              className="p-1 text-gray-400 hover:text-red-600 cursor-pointer"
              title="Nachricht löschen"
            >
              <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Image lightbox */}
      {imageLightbox && imageUrl !== null && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setImageLightbox(false)}
        >
          <img
            src={imageUrl}
            alt="Angehängtes Bild"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      {/* Delete modal */}
      <DeleteChatMessageModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        sessionId={sessionId}
        messageId={message.id}
        onDeleted={onSessionUpdate}
      />
    </div>
  );
}
